import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, TrendingUp, Building2, Wind, Sparkles } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import HomeJournalStrip from "@/components/journal/HomeJournalStrip";
import { TAGLINE, COMPASS_ICON_URL } from "@/lib/brand";

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
            <div className="md:col-span-8">
              <div className="eyebrow mb-6">01 — Wealth, Reimagined as One Strategy</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink">
                One Strategy.<br />
                Two Jobs: <span className="text-brass-deep italic">Build</span> Your Wealth,{" "}
                <span className="text-brass-deep italic">Protect</span> Your Plan.
              </h1>
              <p className="mt-8 text-[16px] md:text-[17px] leading-relaxed text-ink-soft max-w-2xl">
                Most people treat investing and insurance as two separate decisions, handled by two
                different people, bought at two different times. We don't. We build one integrated
                financial strategy for you — where every goal you're working toward is matched with
                the protection that keeps it safe from the things you didn't see coming.
              </p>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/our-strategy" className="btn-brass" data-testid="hero-cta-how">
                  See How It Works <ArrowRight size={16} />
                </Link>
                <a
                  href="https://reliservepulse.com/book-consultation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline"
                  data-testid="hero-cta-talk"
                >
                  Talk to us
                </a>
                <Link to="/snapshot" className="btn-outline" data-testid="hero-cta-snapshot">
                  <Sparkles size={14} /> 30-sec Readiness Snapshot
                </Link>
              </div>
              <a
                href="https://chat.whatsapp.com/GtdGWz5Ydj2D5zTn22qVBx?mode=gi_t"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-5 text-[13.5px] text-brass-deep hover:text-brass transition-colors group"
                data-testid="hero-whatsapp-community"
              >
                <WhatsAppIcon size={14} />
                <span className="border-b border-brass/30 group-hover:border-brass transition-colors">
                  Or join our WhatsApp community
                </span>
              </a>
            </div>

            <div className="md:col-span-4 md:pt-8">
              <div className="card-paper p-7 ledger-rule">
                <div className="eyebrow mb-3">The Tagline</div>
                <p className="font-display text-[22px] leading-snug text-ink">
                  {TAGLINE}
                </p>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <StatTile label="Sides of the plan" value="02" />
                <StatTile label="Review cadence" value="≥ 1× / yr" />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute inset-x-0 -bottom-px h-px bg-line" />
      </section>

      {/* FROM THE JOURNAL — auto-hides when no posts */}
      <HomeJournalStrip limit={2} />

      {/* COMPASS TEASER */}
      <section className="border-t hairline">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-16 md:py-20">
          <Link
            to="/compass"
            className="card-paper block p-8 md:p-12 group"
            data-testid="home-compass-teaser"
          >
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-2 flex md:justify-center">
                <img
                  src={COMPASS_ICON_URL}
                  alt="Reliserve Wealth Compass"
                  className="w-24 h-24 md:w-28 md:h-28 transition-transform duration-500 group-hover:rotate-12"
                />
              </div>
              <div className="md:col-span-8">
                <div className="eyebrow mb-3">Reliserve Wealth Compass</div>
                <h3 className="font-display text-2xl md:text-3xl lg:text-[34px] text-ink leading-tight">
                  Financial literacy shouldn't be a{" "}
                  <em className="italic text-brass-deep">privilege</em> — it should be a{" "}
                  <em className="italic text-brass-deep">starting point</em>.
                </h3>
                <p className="text-ink-soft text-[15.5px] leading-relaxed mt-4 max-w-xl">
                  Through Compass, we bring seminars and webinars on investing and
                  financial planning to students, professionals, and organizations across the globe.
                </p>
              </div>
              <div className="md:col-span-2 md:text-right">
                <span className="link-brass inline-flex items-center gap-1.5 text-[14px]">
                  Learn more <ArrowRight size={14} />
                </span>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* THE IDEA IN ONE LINE */}
      <section className="border-t hairline bg-paper-raised">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="eyebrow">02 — The Idea, In One Line</div>
              <h2 className="font-display text-3xl md:text-4xl mt-4 text-ink leading-tight">
                Every financial life<br />has two sides.
              </h2>
            </div>
            <div className="md:col-span-8">
              <p className="text-[16px] leading-relaxed text-ink-soft">
                They have to work together — not in parallel, and definitely not in isolation.
              </p>

              <div className="grid md:grid-cols-2 gap-5 mt-8">
                <SideCard
                  icon={<TrendingUp size={20} />}
                  label="Building"
                  title="What you're working toward"
                  body="A home. Your child's education, their marriage. A comfortable retirement. The travel you've always wanted."
                />
                <SideCard
                  icon={<ShieldCheck size={20} />}
                  label="Protecting"
                  title="What could derail it"
                  body="The loss of the family's earning member. A disability that stops someone from working. A sudden loss of job or business income."
                />
              </div>

              <p className="mt-8 text-[16px] leading-relaxed text-ink">
                A plan that only does one of these isn't really a plan. We build both into a single
                strategy for you, from day one.
              </p>

              <div className="mt-8 p-6 card-paper ledger-rule">
                <div className="flex items-start gap-3">
                  <Wind className="text-brass shrink-0 mt-1" size={20} />
                  <p className="text-[15px] leading-relaxed text-ink">
                    Running underneath everything is a silent force that makes this harder than it
                    looks — <span className="text-brass-deep font-medium">inflation</span>, which
                    quietly makes your goals costlier and your protection insufficient if left
                    unreviewed. Every strategy we build accounts for it.{" "}
                    <Link to="/why-inflation-matters" className="link-brass">See the numbers →</Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="border-t hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="eyebrow mb-3">Start where every strategy starts</div>
            <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight">
              Income − Expenses = Savings.<br />
              <span className="text-ink-soft">Everything else builds from there.</span>
            </h3>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/our-strategy" className="btn-brass" data-testid="home-cta-strategy">
              See Our Approach <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* GOALS PREVIEW */}
      <section className="border-t hairline bg-paper-raised">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow">03 — Your Goals</div>
              <h2 className="font-display text-3xl md:text-4xl mt-4 text-ink leading-tight">
                Every goal, built and protected —<br />on the same page.
              </h2>
            </div>
            <Link to="/your-goals" className="link-brass text-[14px]">
              See all five goals →
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <GoalPreview id={<Building2 size={18} />} name="Owning a Home" />
            <GoalPreview id={<TrendingUp size={18} />} name="Child's Education" />
            <GoalPreview id={<ShieldCheck size={18} />} name="Retirement" />
          </div>
        </div>
      </section>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="p-4 card-paper">
      <div className="eyebrow">{label}</div>
      <div className="num text-2xl mt-1 text-ink">{value}</div>
    </div>
  );
}

function SideCard({ icon, label, title, body }) {
  return (
    <div className="card-paper p-6 ledger-rule">
      <div className="flex items-center gap-2 text-brass">
        {icon}
        <span className="eyebrow !text-brass-deep">{label}</span>
      </div>
      <div className="font-display text-[20px] mt-3 text-ink leading-snug">{title}</div>
      <p className="text-[14px] text-ink-soft leading-relaxed mt-3">{body}</p>
    </div>
  );
}

function GoalPreview({ id, name }) {
  return (
    <div className="card-paper p-6 hover:border-brass transition-colors">
      <div className="flex items-center gap-2 text-brass">{id}<span className="eyebrow">Goal</span></div>
      <div className="font-display text-[20px] mt-3 text-ink">{name}</div>
      <p className="text-[13px] text-ink-soft mt-2">Build the growth plan. Size the protection. One strategy.</p>
    </div>
  );
}
